import FetchModelAssignments from '@Application/Ai/AiConfiguration/Assignments/Queries/FetchModelAssignments';
import fetchModelAssignments from '@Application/Ai/AiConfiguration/Assignments/fetchModelAssignments';
import FetchAiModels from '@Application/Ai/AiConfiguration/Models/Queries/FetchAiModels';
import fetchAiModels from '@Application/Ai/AiConfiguration/Models/fetchAiModels';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import UsageAssignmentsBoard from '@UI/Pages/AiAssignments/Shared/UsageAssignmentsBoard';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useCurrentFacility';
import { css } from '@emotion/react';
import { MapPin } from 'lucide-react';
import type * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Skeleton, Spinner } from '@verclam/ui-kit';

const spinnerContainerStyle = css`
	display: flex;
	justify-content: center;
	padding: ${theme.layout.gutter};
`;

const catalogSkeletonStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`;

const cardsSkeletonGridStyle = css`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(18rem, 100%), 1fr));
	gap: 1.25rem;
`;

/**
 * Acts on the current facility — the header selector's own choice, never a list to enter. `/facilities*`
 * is mounted outside `RequireFacility` (see `Application.tsx`'s own routes), so this screen resolves the
 * absence of a current facility itself: `isResolved` must be tested alongside `currentFacility === null`,
 * never the second alone — `isResolved` only ever describes the facility resolution, not the
 * organization's, and skipping it would flash "Aucune agence" at someone who has one while the
 * organization/facility context is still settling.
 */
const FacilityAiAssignmentsContent: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const { currentFacility, isResolved } = useCurrentFacility();
	const facilityId = currentFacility?.id ?? null;

	const {
		data: models,
		error: modelsError,
		refetch: refetchModels,
	} = useStoreQuery({
		selector: (state) => state.aiConfigurationStore.models,
		stopOn: (value) => !!value && value.scopeId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAiModels(new FetchAiModels(accountId)));
		},
		deps: [accountId],
	});

	const {
		data: assignments,
		error: assignmentsError,
		refetch: refetchAssignments,
	} = useStoreQuery({
		selector: (state) => state.aiConfigurationStore.facilityAssignments,
		stopOn: (value) => !!value && value.scopeType === 'facility' && value.scopeId === facilityId,
		query: () => {
			if (facilityId === null) {
				return Promise.resolve();
			}

			return dispatch(
				fetchModelAssignments(new FetchModelAssignments({ type: 'facility', facilityId })),
			);
		},
		deps: [facilityId],
	});

	if (!isResolved) {
		return (
			<div css={spinnerContainerStyle}>
				<Spinner aria-label="Résolution de l’agence courante" />
			</div>
		);
	}

	if (currentFacility === null) {
		return (
			<EmptyState
				title="Aucune agence sélectionnée"
				description="Choisissez une agence dans l’en-tête pour configurer ses moteurs."
				icon={<MapPin size={28} />}
			/>
		);
	}

	if (models === null && modelsError === null) {
		return (
			<div css={catalogSkeletonStyle}>
				<Skeleton height="16rem" borderRadius={theme.radii.xlarge} animation="wave" />
			</div>
		);
	}

	if (models === null) {
		return <ErrorState title="Impossible de charger les modèles d’IA." onRetry={refetchModels} />;
	}

	if (assignments === null && assignmentsError === null) {
		return (
			<div css={cardsSkeletonGridStyle}>
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
			</div>
		);
	}

	if (assignments === null) {
		return (
			<ErrorState title="Impossible de charger les modèles de l’agence." onRetry={refetchAssignments} />
		);
	}

	return (
		<UsageAssignmentsBoard
			scope={{ type: 'facility', facilityId: currentFacility.id }}
			assignments={assignments}
			models={models}
		/>
	);
};

export default FacilityAiAssignmentsContent;
